<template>
  <div class="complaint-statistics-container">
    <!-- 顶部筛选 -->
    <el-card class="filter-card">
      <div slot="header"><span>投诉数据统计</span></div>
      <el-form :inline="true" size="small" label-width="80px">
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            style="width:360px"
          />
        </el-form-item>
        <el-form-item label="小区">
          <el-select v-model="communityId" placeholder="请选择小区" style="width:220px" @change="loadStatistics">
            <el-option v-for="c in communityList" :key="c.communityId" :label="c.name" :value="c.communityId" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadStatistics">查询</el-button>
          <el-button @click="resetFilter">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">投诉总数</div>
            <div class="stat-value">{{ stats.total || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">处理中</div>
            <div class="stat-value stat-warning">{{ stats.processing || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">已处理</div>
            <div class="stat-value stat-success">{{ stats.processed || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">平均处理时长</div>
            <div class="stat-value stat-info">{{ stats.avgDurationText || '0分钟' }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 公开率 + 评价 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">公开率</div>
            <div class="stat-value stat-success">{{ stats.publicRate || 0 }}%</div>
            <div class="stat-sub">公开 {{ stats.publicCount || 0 }} / 共 {{ stats.total || 0 }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">评价均分</div>
            <div class="stat-value stat-info">{{ appraisalAvg }}</div>
            <div class="stat-sub">共 {{ appraisalTotal }} 条评价</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表区 -->
    <el-row :gutter="20">
      <el-col :span="12">
        <el-card>
          <div slot="header"><span>新增投诉趋势</span></div>
          <div ref="trendChart" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card>
          <div slot="header"><span>投诉类型分布</span></div>
          <div ref="typeChart" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" style="margin-top:20px">
      <el-col :span="12">
        <el-card>
          <div slot="header"><span>处理人排行</span></div>
          <div ref="handlerChart" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card>
          <div slot="header"><span>评价分布</span></div>
          <div ref="appraisalChart" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import { getCommunityId, getCommunity, getMyEnteredCommunitys } from '@/api/community/communityApi'

export default {
  name: 'ComplaintStatistics',
  data() {
    return {
      dateRange: [],
      communityId: '',
      communityList: [],
      stats: {},
      trendChart: null,
      typeChart: null,
      handlerChart: null,
      appraisalChart: null
    }
  },
  computed: {
    appraisalAvg() {
      const v = this.stats.appraisal && this.stats.appraisal.avgScore
      if (v === 0 || v === undefined || v === null) return '0'
      return Number(v).toFixed(2)
    },
    appraisalTotal() {
      return (this.stats.appraisal && this.stats.appraisal.totalCount) || 0
    }
  },
  mounted() {
    this.communityId = getCommunityId()
    this._loadCommunities()
    this.initCharts()
    window.addEventListener('resize', this.handleResize)
    this.loadStatistics()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
  },
  methods: {
    async _loadCommunities() {
      try {
        const res = await getMyEnteredCommunitys({ page: 1, row: 100 })
        this.communityList = res.communitys || []
      } catch (e) {
        // 兜底使用当前小区
        const cur = getCommunity()
        this.communityList = cur ? [cur] : []
      }
    },
    initCharts() {
      this.trendChart = echarts.init(this.$refs.trendChart)
      this.typeChart = echarts.init(this.$refs.typeChart)
      this.handlerChart = echarts.init(this.$refs.handlerChart)
      this.appraisalChart = echarts.init(this.$refs.appraisalChart)
    },
    disposeCharts() {
      if (this.trendChart) this.trendChart.dispose()
      if (this.typeChart) this.typeChart.dispose()
      if (this.handlerChart) this.handlerChart.dispose()
      if (this.appraisalChart) this.appraisalChart.dispose()
    },
    handleResize() {
      if (this.trendChart) this.trendChart.resize()
      if (this.typeChart) this.typeChart.resize()
      if (this.handlerChart) this.handlerChart.resize()
      if (this.appraisalChart) this.appraisalChart.resize()
    },
    buildParams() {
      const params = {
        communityId: this.communityId,
        page: 1,
        row: 1
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0]
        params.endTime = this.dateRange[1]
      }
      return params
    },
    async loadStatistics() {
      try {
        const params = this.buildParams()
        const res = await request({
          url: '/complaint.queryComplaintStatistics',
          method: 'get',
          params
        })
        const data = res.data
        if (data.code === 0) {
          this.stats = data.data || {}
          this.renderCharts()
        } else {
          this.$message.error(data.msg || '查询统计失败')
        }
      } catch (e) {
        console.error('查询投诉统计失败', e)
        this.$message.error('查询投诉统计失败')
      }
    },
    resetFilter() {
      this.dateRange = []
      this.communityId = getCommunityId()
      this.loadStatistics()
    },
    renderCharts() {
      this.renderTrendChart()
      this.renderTypeChart()
      this.renderHandlerChart()
      this.renderAppraisalChart()
    },
    renderTrendChart() {
      if (!this.trendChart) return
      const byDay = this.stats.byDay || []
      const days = byDay.map(i => i.day)
      const counts = byDay.map(i => i.count)
      this.trendChart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 40, right: 20, top: 30, bottom: 30 },
        xAxis: { type: 'category', data: days, axisLabel: { rotate: 30 } },
        yAxis: { type: 'value', minInterval: 1 },
        series: [{ name: '新增投诉', type: 'line', smooth: true, data: counts, itemStyle: { color: '#409eff' }, areaStyle: { color: 'rgba(64,158,255,0.15)' } }]
      })
    },
    renderTypeChart() {
      if (!this.typeChart) return
      const byType = this.stats.byType || []
      const data = byType.map(i => ({ name: i.typeName || '未知', value: i.count }))
      this.typeChart.setOption({
        tooltip: { trigger: 'item', formatter: '{a} <br/>{b}: {c} ({d}%)' },
        legend: { bottom: 0 },
        series: [{ name: '类型分布', type: 'pie', radius: ['40%', '65%'], data: data, itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 } }]
      })
    },
    renderHandlerChart() {
      if (!this.handlerChart) return
      const byHandler = this.stats.byHandler || []
      const names = byHandler.map(i => i.handlerName || '未知')
      const counts = byHandler.map(i => i.count)
      this.handlerChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 40, right: 20, top: 30, bottom: 30 },
        xAxis: { type: 'category', data: names, axisLabel: { rotate: 30 } },
        yAxis: { type: 'value', minInterval: 1 },
        series: [{ name: '处理数量', type: 'bar', data: counts, itemStyle: { color: '#67c23a' } }]
      })
    },
    renderAppraisalChart() {
      if (!this.appraisalChart) return
      const distribution = (this.stats.appraisal && this.stats.appraisal.distribution) || []
      const names = distribution.map(i => i.score + '星')
      const counts = distribution.map(i => i.count)
      this.appraisalChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 40, right: 20, top: 30, bottom: 30 },
        xAxis: { type: 'category', data: names },
        yAxis: { type: 'value', minInterval: 1 },
        series: [{ name: '评价数', type: 'bar', data: counts, itemStyle: { color: '#e6a23c' } }]
      })
    }
  }
}
</script>

<style scoped>
.complaint-statistics-container {
  padding: 20px;
}
.filter-card {
  margin-bottom: 20px;
}
.stat-cards {
  margin-bottom: 20px;
}
.stat-item {
  text-align: center;
  padding: 10px 0;
}
.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}
.stat-value {
  font-size: 28px;
  font-weight: 600;
  color: #303133;
}
.stat-warning {
  color: #e6a23c;
}
.stat-success {
  color: #67c23a;
}
.stat-info {
  color: #409eff;
}
.stat-sub {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 6px;
}
.chart-box {
  height: 320px;
  width: 100%;
}
</style>
